import { GoogleGenerativeAI } from '@google/generative-ai';

const genAI = new GoogleGenerativeAI(import.meta.env.VITE_GEMINI_API_KEY);

export interface FlashCard {
  term: string;
  definition: string;
  category?: string;
}

export interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: string;
  explanation: string;
}

export interface StudyContent {
  flashcards: FlashCard[];
  questions: QuizQuestion[];
}

export async function generateStudyContent(
  extractedText: string,
  contentType: 'flashcards' | 'quiz' | 'both' = 'both'
): Promise<StudyContent> {
  try {
    // Check if API key is available
    if (!import.meta.env.VITE_GEMINI_API_KEY) {
      throw new Error('Gemini API key is not configured. Please set VITE_GEMINI_API_KEY in your environment variables.');
    }

    const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash' });

    // Intelligent text processing for large documents
    const processedText = await processLargeText(extractedText, model);

    const prompt = `
      Analyze the following text and generate comprehensive educational content:
      
      ${processedText}
      
      Please respond with a JSON object containing:
      ${contentType === 'flashcards' || contentType === 'both' ? `
      - "flashcards": Array of flashcard objects with "term" and "definition" fields
      ` : ''}
      ${contentType === 'quiz' || contentType === 'both' ? `
      - "questions": Array of quiz questions with "question", "options" (4 choices), "correctAnswer", and "explanation" fields
      ` : ''}
      
      Make sure to:
      - Extract the most important concepts and facts from the ENTIRE document
      - Create clear, concise definitions
      - Generate diverse question types (multiple choice)
      - Provide helpful explanations for quiz answers
      - Ensure comprehensive coverage of the material
      - Limit to 30 flashcards and 25 quiz questions maximum for better coverage
      
      Return only valid JSON without any additional text or formatting.
    `;

    const result = await model.generateContent(prompt);
    const response = await result.response;
    const text = response.text();

    // Clean up the response to extract JSON
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error('No valid JSON found in response');
    }

    const studyContent = JSON.parse(jsonMatch[0]);

    // Validate the structure
    if (!studyContent.flashcards && !studyContent.questions) {
      throw new Error('Invalid response structure');
    }

    return {
      flashcards: studyContent.flashcards || [],
      questions: studyContent.questions || []
    };
  } catch (error) {
    console.error('Error generating study content:', error);
    
    // Provide more specific error messages
    if (error instanceof Error) {
      const errorMessage = error.message.toLowerCase();
      
      if (errorMessage.includes('api key') || errorMessage.includes('authentication')) {
        throw new Error('Gemini API key is invalid or not configured. Please check your VITE_GEMINI_API_KEY environment variable.');
      } else if (errorMessage.includes('model') || errorMessage.includes('not found')) {
        throw new Error('Gemini model is not available. Please check your API configuration.');
      } else if (errorMessage.includes('quota') || errorMessage.includes('limit') || errorMessage.includes('429')) {
        throw new Error('API quota exceeded. The free tier has daily and per-minute limits. Please wait a few minutes and try again, or upgrade to a paid plan for higher limits.');
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        throw new Error('Network error connecting to Gemini API. Please check your internet connection and try again.');
      } else {
        throw new Error(`Failed to generate study content: ${error.message}`);
      }
    }
    
    throw new Error('Failed to generate study content. Please try again.');
  }
}

// Helper function to process large text intelligently
async function processLargeText(text: string, model: any): Promise<string> {
  const maxChunkSize = 30000; // Gemini's recommended input size
  const textLength = text.length;
  
  console.log(`Processing text of ${textLength} characters`);
  
  // If text is small enough, return as is
  if (textLength <= maxChunkSize) {
    return text;
  }
  
  // For large documents, create a comprehensive summary
  console.log('Text is large, creating intelligent summary...');
  
  try {
    // Split text into chunks and create summaries
    const chunks = splitTextIntoChunks(text, maxChunkSize);
    const summaries: string[] = [];
    
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      const summaryPrompt = `
        Create a comprehensive summary of this section of the document, preserving all important concepts, facts, and details:
        
        ${chunk}
        
        Focus on:
        - Key concepts and definitions
        - Important facts and data
        - Main ideas and themes
        - Technical terms and their explanations
        
        Make the summary detailed enough to generate study materials from.
      `;
      
      const result = await model.generateContent(summaryPrompt);
      const response = await result.response;
      summaries.push(response.text());
      
      // Small delay to prevent rate limiting
      if (i < chunks.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    // Combine summaries into final processed text
    const combinedSummary = summaries.join('\n\n');
    console.log(`Created comprehensive summary of ${combinedSummary.length} characters from ${chunks.length} chunks`);
    
    return combinedSummary;
    
  } catch (error) {
    console.warn('Failed to create intelligent summary, using first portion of text:', error);
    // Fallback: use the first portion of the text
    return text.substring(0, maxChunkSize);
  }
}

// Helper function to split text into manageable chunks
function splitTextIntoChunks(text: string, maxChunkSize: number): string[] {
  const chunks: string[] = [];
  let currentChunk = '';
  
  // Split by paragraphs to maintain context
  const paragraphs = text.split('\n\n');
  
  for (const paragraph of paragraphs) {
    if ((currentChunk + paragraph).length > maxChunkSize && currentChunk.length > 0) {
      chunks.push(currentChunk.trim());
      currentChunk = paragraph;
    } else {
      currentChunk += (currentChunk ? '\n\n' : '') + paragraph;
    }
  }
  
  // Add the last chunk
  if (currentChunk.trim()) {
    chunks.push(currentChunk.trim());
  }
  
  return chunks;
}

export async function generateSummary(text: string): Promise<string> {
  try {
    // Check if API key is available
    if (!import.meta.env.VITE_GEMINI_API_KEY) {
      throw new Error('Gemini API key is not configured. Please set VITE_GEMINI_API_KEY in your environment variables.');
    }

    const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash' });

    const prompt = `
      Create a concise summary of the following text, highlighting the main points and key concepts:
      
      ${text}
      
      Keep the summary between 100-200 words and focus on the most important information.
    `;

    const result = await model.generateContent(prompt);
    const response = await result.response;
    return response.text();
  } catch (error) {
    console.error('Error generating summary:', error);
    
    // Provide more specific error messages
    if (error instanceof Error) {
      const errorMessage = error.message.toLowerCase();
      
      if (errorMessage.includes('api key') || errorMessage.includes('authentication')) {
        throw new Error('Gemini API key is invalid or not configured. Please check your VITE_GEMINI_API_KEY environment variable.');
      } else if (errorMessage.includes('model') || errorMessage.includes('not found')) {
        throw new Error('Gemini model is not available. Please check your API configuration.');
      } else if (errorMessage.includes('quota') || errorMessage.includes('limit') || errorMessage.includes('429')) {
        throw new Error('API quota exceeded. The free tier has daily and per-minute limits. Please wait a few minutes and try again, or upgrade to a paid plan for higher limits.');
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        throw new Error('Network error connecting to Gemini API. Please check your internet connection and try again.');
      } else {
        throw new Error(`Failed to generate summary: ${error.message}`);
      }
    }
    
    throw new Error('Failed to generate summary. Please try again.');
  }
}

// Test function to verify API configuration
export async function testGeminiAPI(): Promise<boolean> {
  try {
    if (!import.meta.env.VITE_GEMINI_API_KEY) {
      console.error('❌ VITE_GEMINI_API_KEY is not set');
      return false;
    }

    console.log('🔑 API Key found:', import.meta.env.VITE_GEMINI_API_KEY.substring(0, 10) + '...');
    
    const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash' });
    const result = await model.generateContent('Hello, this is a test message.');
    const response = await result.response;
    const text = response.text();
    
    console.log('✅ Gemini API test successful:', text);
    return true;
  } catch (error) {
    console.error('❌ Gemini API test failed:', error);
    return false;
  }
}

// Make test function available globally
if (typeof window !== 'undefined') {
  (window as any).testGeminiAPI = testGeminiAPI;
}